import React, { useRef, useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';

interface Props {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  onComplete?: (value: string) => void;
  onEnter?: () => void;
}

const OtpInput: React.FC<Props> = ({
  length = 6,
  value,
  onChange,
  disabled = false,
  error = false,
  onComplete,
  onEnter,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    // Focus first empty input
    const firstEmptyIndex = value.length < length ? value.length : length - 1;
    if (inputRefs.current[firstEmptyIndex] && !disabled) {
      inputRefs.current[firstEmptyIndex]?.focus();
    }
  }, [value, length, disabled]);

  const handleChange = (index: number, newValue: string) => {
    // Only allow digits
    const digit = newValue.replace(/\D/g, '').slice(0, 1);
    
    if (digit) {
      // Build the code array ensuring it's always the correct length
      const currentCodeArray = value.split('').slice(0, length);
      // Pad to full length if needed
      while (currentCodeArray.length < length) {
        currentCodeArray.push('');
      }
      // Set the digit at the correct index
      currentCodeArray[index] = digit;
      // Join and ensure we have exactly the right length
      const updatedCode = currentCodeArray.slice(0, length).join('');
      onChange(updatedCode);
      
      // Move to next input if available
      if (index < length - 1) {
        setTimeout(() => {
          inputRefs.current[index + 1]?.focus();
        }, 0);
      }
      
      // Call onComplete if all digits are filled (use setTimeout to ensure state is updated)
      // Check that all positions are filled (no empty strings)
      const allFilled = updatedCode.length === length && currentCodeArray.every(d => d !== '');
      if (allFilled && onComplete) {
        setTimeout(() => {
          onComplete(updatedCode);
        }, 50);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        // Clear current digit
        const newCode = value.split('');
        newCode[index] = '';
        onChange(newCode.join(''));
      } else if (index > 0) {
        // Move to previous input and clear it
        const newCode = value.split('');
        newCode[index - 1] = '';
        onChange(newCode.join(''));
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter' && value.length === length && onEnter) {
      // Allow Enter key to submit when all digits are filled
      onEnter();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      // Focus the next empty input or the last one
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, index) => (
        <TextField
          key={index}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 500,
              padding: 0,
              width: '48px',
              height: '56px',
            },
          }}
          sx={{
            width: '48px',
            '& .MuiOutlinedInput-root': {
              height: '56px',
              '& fieldset': {
                borderColor: error
                  ? '#d32f2f'
                  : focusedIndex === index
                  ? '#1976d2'
                  : '#e0e0e0',
                borderWidth: '1.5px',
                borderRadius: '8px',
              },
              '&:hover fieldset': {
                borderColor: error ? '#d32f2f' : '#bdbdbd',
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? '#d32f2f' : '#1976d2',
                borderWidth: '2px',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;

